// src/components/CitationPopover.tsx
// Interactive popover for evidence citations in source-grounded proposals

import React, { useState, useRef, useEffect, ReactNode } from 'react';
import {
  X,
  ExternalLink,
  ChevronRight,
  Database,
  User,
  FileText,
  Award,
  DollarSign,
  Briefcase,
  Link2,
  TrendingUp,
} from 'lucide-react';
import { EvidenceSourceType, SourceCitation, ScoredEvidenceItem } from '../types/source-grounded';
import { RelevanceTier } from '../lib/taxonomy/types';

// ============================================================
// TYPES
// ============================================================

interface CitationPopoverProps {
  citation: SourceCitation;
  evidence?: ScoredEvidenceItem;
  onNavigateToProfile?: (section: string) => void;
  position?: { x: number; y: number };
  onClose: () => void;
}

interface EvidenceDetails {
  evidenceId: string;
  sourceType: EvidenceSourceType;
  summary: string;
  fullContent?: string;
  relevance?: {
    tier: RelevanceTier;
    score: number;
  };
  isInferred?: boolean;
  inferenceChain?: string;
  metadata?: Record<string, unknown>;
}

// ============================================================
// HELPERS
// ============================================================

const getSourceIcon = (sourceType: EvidenceSourceType) => {
  switch (sourceType) {
    case 'past_performance':
      return Briefcase;
    case 'personnel':
      return User;
    case 'certification':
      return Award;
    case 'document':
      return FileText;
    case 'capability':
      return TrendingUp;
    case 'pricing':
      return DollarSign;
    case 'profile':
    default:
      return Database;
  }
};

const getSourceLabel = (sourceType: EvidenceSourceType): string => {
  const labels: Record<EvidenceSourceType, string> = {
    profile: 'Company Profile',
    past_performance: 'Past Performance',
    personnel: 'Key Personnel',
    certification: 'Certification',
    document: 'Document',
    capability: 'Capability',
    pricing: 'Pricing Data',
  };
  return labels[sourceType] || 'Evidence';
};

const getRelevanceColor = (tier: RelevanceTier | string): string => {
  switch (tier) {
    case 'HIGH':
      return 'text-success bg-success-bg border-success-border';
    case 'MEDIUM':
      return 'text-fit-moderate bg-fit-moderate-bg border-fit-moderate-border';
    case 'LOW':
      return 'text-fit-moderate bg-orange-50 border-orange-200';
    case 'MINIMAL':
    default:
      return 'text-bw-ink bg-bw-surface-subtle border-bw-border';
  }
};

const getProfileSection = (sourceType: EvidenceSourceType): string => {
  const sectionMap: Record<EvidenceSourceType, string> = {
    profile: 'company',
    past_performance: 'past-performance',
    personnel: 'personnel',
    certification: 'certifications',
    document: 'documents',
    capability: 'capabilities',
    pricing: 'pricing',
  };
  return sectionMap[sourceType] || 'profile';
};

// ============================================================
// COMPONENT
// ============================================================

export function CitationPopover({
  citation,
  evidence,
  onNavigateToProfile,
  position,
  onClose,
}: CitationPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [showFullContent, setShowFullContent] = useState(false);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Position styles
  const positionStyles = position
    ? {
        position: 'fixed' as const,
        left: Math.min(position.x, window.innerWidth - 400),
        top: Math.min(position.y + 10, window.innerHeight - 300),
      }
    : {};

  const SourceIcon = getSourceIcon(citation.evidenceType);
  const relevanceTier = evidence?.relevance?.tier || 'MEDIUM';
  const relevanceScore = evidence?.relevance?.overallScore || 0;

  return (
    <div
      ref={popoverRef}
      className="z-50 w-96 bg-bw-surface rounded-lg shadow-xl border border-bw-border overflow-hidden"
      style={positionStyles}
    >
      {/* Header */}
      <div className="bg-bw-surface-subtle px-4 py-3 border-b border-bw-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-bw-surface rounded-md border border-bw-border">
              <SourceIcon className="h-4 w-4 text-bw-muted" />
            </div>
            <div>
              <code className="text-sm font-mono font-semibold text-bw-ink">
                [{citation.evidenceId}]
              </code>
              <p className="text-xs text-bw-muted">{getSourceLabel(citation.evidenceType)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Summary */}
        <div>
          <p className="text-sm text-bw-ink leading-relaxed">{citation.evidenceSummary}</p>
        </div>

        {/* Full Content (expandable) */}
        {evidence?.fullContent && (
          <div>
            <button
              onClick={() => setShowFullContent(!showFullContent)}
              className="flex items-center gap-1 text-xs text-bw-primary hover:text-bw-primary font-medium"
            >
              <ChevronRight
                className={`h-3 w-3 transition-transform ${showFullContent ? 'rotate-90' : ''}`}
              />
              {showFullContent ? 'Hide details' : 'Show full details'}
            </button>
            {showFullContent && (
              <div className="mt-2 p-3 bg-bw-surface-subtle rounded-md border border-bw-border max-h-32 overflow-y-auto">
                <p className="text-xs text-bw-muted whitespace-pre-wrap">
                  {evidence.fullContent.substring(0, 500)}
                  {evidence.fullContent.length > 500 && '...'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Relevance Score */}
        {evidence?.relevance && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-bw-muted">Contract Relevance</span>
            <div className="flex items-center gap-2">
              <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    relevanceScore >= 0.7
                      ? 'bg-success-bg0'
                      : relevanceScore >= 0.5
                      ? 'bg-fit-moderate-bg0'
                      : 'bg-orange-500'
                  }`}
                  style={{ width: `${relevanceScore * 100}%` }}
                />
              </div>
              <span
                className={`text-xs font-medium px-1.5 py-0.5 rounded border ${getRelevanceColor(
                  relevanceTier
                )}`}
              >
                {relevanceTier}
              </span>
            </div>
          </div>
        )}

        {/* Inference Chain (if inferred) */}
        {citation.isInferred && citation.inferenceChain && (
          <div className="p-3 bg-bw-primary-tint rounded-md border border-purple-100">
            <div className="flex items-start gap-2">
              <Link2 className="h-4 w-4 text-bw-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-bw-primary">Inferred Capability</p>
                <p className="text-xs text-bw-primary mt-1">{citation.inferenceChain}</p>
              </div>
            </div>
          </div>
        )}

        {/* Keywords */}
        {evidence?.keywords && evidence.keywords.length > 0 && (
          <div>
            <p className="text-xs text-bw-muted mb-1.5">Keywords</p>
            <div className="flex flex-wrap gap-1">
              {evidence.keywords.slice(0, 8).map((keyword, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-0.5 bg-bw-surface-subtle text-bw-muted rounded-full"
                >
                  {keyword}
                </span>
              ))}
              {evidence.keywords.length > 8 && (
                <span className="text-xs text-bw-muted">
                  +{evidence.keywords.length - 8} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {onNavigateToProfile && (
        <div className="px-4 py-3 bg-bw-surface-subtle border-t border-bw-border">
          <button
            onClick={() => onNavigateToProfile(getProfileSection(citation.evidenceType))}
            className="flex items-center justify-center w-full gap-2 px-3 py-2 text-sm font-medium text-bw-primary hover:text-bw-primary hover:bg-bw-primary-tint rounded-md transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Edit in Profile
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// INLINE CITATION COMPONENT
// ============================================================

interface InlineCitationProps {
  evidenceId: string;
  onClick: (evidenceId: string, event: React.MouseEvent) => void;
  isActive?: boolean;
}

export function InlineCitation({ evidenceId, onClick, isActive }: InlineCitationProps) {
  return (
    <button
      onClick={(e) => onClick(evidenceId, e)}
      className={`inline-flex items-center px-1 py-0.5 text-xs font-mono rounded transition-colors ${
        isActive
          ? 'bg-evidence-grounded-bg text-evidence-grounded ring-2 ring-evidence-grounded-border'
          : 'bg-evidence-grounded-bg text-evidence-grounded hover:brightness-95'
      }`}
    >
      [{evidenceId}]
    </button>
  );
}

// ============================================================
// CITATION HIGHLIGHT WRAPPER
// ============================================================

interface CitationHighlightProps {
  content: string;
  citations: SourceCitation[];
  evidence?: Map<string, ScoredEvidenceItem>;
  onNavigateToProfile?: (section: string) => void;
  showCitations?: boolean;
}

// Import ReactMarkdown and remark-gfm
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export function CitationHighlight({
  content,
  citations,
  evidence,
  onNavigateToProfile,
  showCitations = true,
}: CitationHighlightProps) {
  const [activeCitation, setActiveCitation] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);

  // Strip citations if not showing them
  let processedContent = showCitations
    ? content
    : content.replace(/\s*\[[A-Z]+-\d+(?:,\s*[^\]]+)?]\s*/gi, ' ');

  // CRITICAL: Ensure proper line breaks for markdown parsing
  // Sometimes content comes with escaped newlines or spaces instead of real line breaks
  processedContent = processedContent
    .replace(/\\n/g, '\n')           // Replace escaped newlines
    .replace(/\r\n/g, '\n')          // Normalize Windows line endings
    .replace(/\. ([A-Z#])/g, '.\n\n$1') // Add paragraph breaks after sentences before headers
    .replace(/([^|\n])\|([^|\n])/g, '$1\n|$2') // Ensure table rows are on separate lines
    .replace(/\n{3,}/g, '\n\n');     // Collapse multiple newlines

  // Custom component to render text nodes with citation handling
  const TextWithCitations = ({ children }: { children: string }) => {
    if (!showCitations || typeof children !== 'string') {
      return <>{children}</>;
    }

    const parts: React.ReactNode[] = [];
    const pattern = /\[([A-Z]+-\d+)(?:,\s*([^\]]+))?\]/g;
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(children)) !== null) {
      // Add text before citation
      if (match.index > lastIndex) {
        parts.push(children.substring(lastIndex, match.index));
      }

      const evidenceId = match[1];

      parts.push(
        <InlineCitation
          key={`${evidenceId}-${match.index}`}
          evidenceId={evidenceId}
          isActive={activeCitation === evidenceId}
          onClick={(id, event) => {
            setActiveCitation(id);
            setPopoverPosition({ x: event.clientX, y: event.clientY });
          }}
        />
      );

      lastIndex = pattern.lastIndex;
    }

    // Add remaining text
    if (lastIndex < children.length) {
      parts.push(children.substring(lastIndex));
    }

    return <>{parts}</>;
  };

  // Custom markdown components that handle citations in text
  const markdownComponents = {
    p: ({ children }: { children: React.ReactNode }) => (
      <p className="mb-4 leading-relaxed text-slate-700">
        {React.Children.map(children, (child) =>
          typeof child === 'string' ? <TextWithCitations>{child}</TextWithCitations> : child
        )}
      </p>
    ),
    li: ({ children }: { children: React.ReactNode }) => (
      <li className="mb-2">
        {React.Children.map(children, (child) =>
          typeof child === 'string' ? <TextWithCitations>{child}</TextWithCitations> : child
        )}
      </li>
    ),
    h1: ({ children }: { children: React.ReactNode }) => (
      <h1 className="text-2xl font-bold mt-8 mb-4 text-slate-900 border-b border-slate-200 pb-2">{children}</h1>
    ),
    h2: ({ children }: { children: React.ReactNode }) => (
      <h2 className="text-xl font-bold mt-6 mb-3 text-slate-800">{children}</h2>
    ),
    h3: ({ children }: { children: React.ReactNode }) => (
      <h3 className="text-lg font-semibold mt-5 mb-2 text-slate-700">{children}</h3>
    ),
    h4: ({ children }: { children: React.ReactNode }) => (
      <h4 className="text-base font-semibold mt-4 mb-2 text-slate-700">{children}</h4>
    ),
    strong: ({ children }: { children: React.ReactNode }) => (
      <strong className="font-semibold text-slate-900">{children}</strong>
    ),
    em: ({ children }: { children: React.ReactNode }) => (
      <em className="italic text-slate-600">{children}</em>
    ),
    ul: ({ children }: { children: React.ReactNode }) => (
      <ul className="list-disc ml-6 mb-4 space-y-1">{children}</ul>
    ),
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol className="list-decimal ml-6 mb-4 space-y-1">{children}</ol>
    ),
    table: ({ children }: { children: React.ReactNode }) => (
      <div className="overflow-x-auto my-4 border border-slate-200 rounded-lg">
        <table className="min-w-full divide-y divide-slate-200">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: { children: React.ReactNode }) => (
      <thead className="bg-slate-100">{children}</thead>
    ),
    tbody: ({ children }: { children: React.ReactNode }) => (
      <tbody className="bg-bw-surface divide-y divide-slate-100">{children}</tbody>
    ),
    th: ({ children }: { children: React.ReactNode }) => (
      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
        {children}
      </th>
    ),
    td: ({ children }: { children: React.ReactNode }) => (
      <td className="px-4 py-3 text-sm text-slate-600 whitespace-normal">
        {React.Children.map(children, (child) =>
          typeof child === 'string' ? <TextWithCitations>{child}</TextWithCitations> : child
        )}
      </td>
    ),
    tr: ({ children }: { children: React.ReactNode }) => (
      <tr className="hover:bg-slate-50 transition-colors">{children}</tr>
    ),
    blockquote: ({ children }: { children: React.ReactNode }) => (
      <blockquote className="border-l-4 border-blue-300 pl-4 py-2 my-4 bg-bw-primary-tint rounded-r-lg italic text-slate-700">
        {children}
      </blockquote>
    ),
    code: ({ children, className }: { children: React.ReactNode; className?: string }) => {
      const isInline = !className;
      return isInline ? (
        <code className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-sm font-mono">
          {children}
        </code>
      ) : (
        <code className="block p-4 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto text-sm font-mono">
          {children}
        </code>
      );
    },
    hr: () => <hr className="my-6 border-t border-slate-200" />,
    a: ({ href, children }: { href?: string; children: React.ReactNode }) => (
      <a href={href} className="text-bw-primary hover:text-bw-primary underline" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  };

  const activeCitationData = citations.find((c) => c.evidenceId === activeCitation);
  const activeEvidence = activeCitation ? evidence?.get(activeCitation) : undefined;

  return (
    <div className="relative prose prose-slate max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents as any}>
        {processedContent}
      </ReactMarkdown>

      {/* Popover */}
      {activeCitation && activeCitationData && popoverPosition && (
        <CitationPopover
          citation={activeCitationData}
          evidence={activeEvidence}
          position={popoverPosition}
          onNavigateToProfile={onNavigateToProfile}
          onClose={() => {
            setActiveCitation(null);
            setPopoverPosition(null);
          }}
        />
      )}
    </div>
  );
}
